/**
 * AiCloudHome - Dashboard ana sayfası
 * RunAI OS V7 Masterpiece - Pixel Perfect Reconstruction
 */

import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
// import { useNavigate } from 'react-router-dom'
import { API_URL } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import { useOSTheme } from '../../components/os/OSThemeContext'
import CodePenBackground from '../../components/os/CodePenBackground'
import ForceGraphViewer from '../../components/os/ForceGraphViewer'

// --- Types ---
interface FlzBalance { balance: number; total_earned: number; total_spent: number }
interface ApiKey {
  id: number; provider: string; is_active: boolean; total_tokens_used: number
}
interface Agent {
  id: number; agent_name: string; status: string
}
// Skill interface is removed as it's not currently used in this view
// ActivityItem interface is available if needed for future activity lists

// --- Helpers ---
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` })
async function apiFetch<T>(path: string): Promise<T> {
  const res = await axios.get<T>(`${API_URL}${path}`, { headers: authHdr() })
  return res.data
}

export default function AiCloudHome() {
  const { theme } = useOSTheme()
  const isDark = theme === 'dark'
  const { user } = useAuth()
  const [flz, setFlz] = useState<FlzBalance>({ balance: 0, total_earned: 0, total_spent: 0 })
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [input, setInput] = useState('')
  const [chat, setChat] = useState<{ role: string; content: string; ts: string; agentName?: string }[]>([
    { role: 'assistant', content: 'Merkez Komutaya Hoşgeldiniz. Bir komut verin veya analiz isteyin.', ts: new Date().toLocaleTimeString(), agentName: 'Baba Filiz' }
  ])
  const [activeAgent, setActiveAgent] = useState<Agent | null>(null)
  const [inspectNode, setInspectNode] = useState<any>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const load = async () => {
      try {
        const [flzRes, keysRes, agentsRes] = await Promise.allSettled([
          apiFetch<FlzBalance>('/api/flz/balance'),
          apiFetch<{ keys: ApiKey[] }>('/api/aicloud/api-keys'),
          apiFetch<Agent[] | { agents: Agent[] }>('/api/agents'),
          // apiFetch<Skill[] | { skills: Skill[] }>('/api/aicloud/skills/installed')
        ])

        if (flzRes.status === 'fulfilled') setFlz(flzRes.value)
        if (keysRes.status === 'fulfilled') setApiKeys(keysRes.value?.keys ?? [])
        if (agentsRes.status === 'fulfilled') {
          const v = agentsRes.value
          const arr = Array.isArray(v) ? v : ((v as { agents?: Agent[] }).agents ?? [])
          setAgents(arr)
          if (arr.length > 0) setActiveAgent(arr[0])
        }
      } catch (e) {
        console.error("Load failed", e)
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chat])

  const send = async () => {
    if (!input.trim()) return
    const msg = input.trim()
    setInput('')
    setChat(prev => [...prev, { role: 'user', content: msg, ts: new Date().toLocaleTimeString() }])

    try {
      const res = await axios.post(`${API_URL}/api/chat/send`, {
        agent_id: activeAgent?.id || 1,
        message: msg
      }, { headers: authHdr() })
      const reply = res.data?.response || "İşlem tamamlandı."
      setChat(prev => [...prev, { role: 'assistant', content: reply, ts: new Date().toLocaleTimeString(), agentName: activeAgent?.agent_name || 'Baba Filiz' }])
    } catch (e) {
      setChat(prev => [...prev, { role: 'assistant', content: "Hata oluştu.", ts: new Date().toLocaleTimeString(), agentName: 'System' }])
    }
  }

  if (loading) return <div className="flex h-screen items-center justify-center font-headline text-slate-500">Initializing OS Core...</div>

  const glassClass = isDark ? "backdrop-blur-[24px] bg-[#0a0d12]/70 border border-cyan-400/20 shadow-[0_10px_40px_rgba(0,0,0,0.5)]" : "backdrop-blur-[24px] bg-white/70 border border-black/[0.05] shadow-[0_4px_20px_rgba(0,0,0,0.03)]";
  const glassStrong = isDark ? "backdrop-blur-[32px] bg-black/80 border border-cyan-400/30" : "backdrop-blur-[30px] bg-white/90 border border-blue-500/15 shadow-[0_10px_40px_rgba(0,0,0,0.08)]";

  return (
    <div className={`fixed inset-0 flex flex-col font-headline overflow-hidden ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
      
      <CodePenBackground />

      {/* TOP ORCHESTRATOR BAR */}
      <header className={`h-14 m-4 px-6 ${glassClass} rounded-2xl flex items-center justify-between z-50`}>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-500 animate-pulse shadow-[0_0_10px_rgba(59,130,246,0.5)]"></span>
            <span className={`text-xl font-bold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r ${isDark ? 'from-cyan-400 to-purple-400' : 'from-blue-600 to-purple-600'}`}>RUNAI.OS</span>
          </div>
          
          <div className={`flex items-center gap-3 px-4 py-1.5 rounded-full border ${isDark ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-slate-100 border-purple-500/20'}`}>
            <span className={`text-[10px] uppercase tracking-widest font-bold ${isDark ? 'text-cyan-400' : 'text-purple-600'}`}>Mic Active</span>
            <div className="flex items-center gap-1">
              <div className="w-1 h-3 bg-purple-500 rounded-full animate-bounce"></div>
              <div className="w-1 h-5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-1 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 border border-green-200 rounded-lg text-green-700 text-[11px] font-mono font-bold shadow-sm">
            <span>{flz.balance.toFixed(0)} FLZ</span>
          </div>
          <div className="flex -space-x-3 items-center">
            <div className="w-8 h-8 rounded-full border-2 border-white overflow-hidden shadow-md bg-slate-200">
               {user?.avatar ? <img className="w-full h-full object-cover" src={user.avatar} alt="u"/> : <div className="w-full h-full flex items-center justify-center text-[10px] font-bold">G</div>}
            </div>
            <div className="w-8 h-8 rounded-full border-2 border-white overflow-hidden shadow-md bg-purple-500 flex items-center justify-center text-white text-[10px] font-bold">A</div>
          </div>
        </div>
      </header>

      <main className="flex-1 flex px-4 pb-4 gap-4 overflow-hidden z-40">
        
        {/* LEFT PANEL */}
        <aside className={`w-[280px] ${glassClass} rounded-3xl p-5 flex flex-col gap-6 overflow-y-auto hide-scrollbar`}>
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-[10px] uppercase tracking-widest font-bold ${isDark ? 'text-cyan-400' : 'text-blue-600'}`}>Cloud Engines</h3>
              <span className={`text-xs font-mono font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>$0.024/hr</span>
            </div>
            
            <div className="space-y-3">
              {apiKeys.slice(0, 2).map((ak, idx) => (
                <div key={ak.id} className={`${isDark ? 'bg-white/5' : 'bg-white/50'} p-3 rounded-xl border ${isDark ? 'border-white/10' : 'border-slate-200'} hover:border-blue-500/50 transition`}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2 text-xs font-bold"><span className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-orange-500' : 'bg-blue-500'}`}></span> {ak.provider}</div>
                    <span className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-bold">Active</span>
                  </div>
                  <div className="flex gap-4 text-[10px] font-mono text-slate-500 mb-2">
                    <div>Load: {idx === 0 ? '78' : '45'}%</div> <div>{idx === 0 ? '18' : '94'}ms</div>
                  </div>
                  <div className={`h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                    <div className={`h-full ${idx === 0 ? 'bg-orange-500' : 'bg-blue-500'}`} style={{ width: idx === 0 ? '78%' : '45%' }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-[10px] uppercase tracking-widest font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Agent Hub</h3>
              <span className="text-[10px] font-mono text-slate-500">{agents.length} Total</span>
            </div>

            <div className="space-y-2">
              {agents.slice(0, 3).map((ag, idx) => (
                <div key={ag.id} onClick={() => setActiveAgent(ag)} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-all ${activeAgent?.id === ag.id ? (isDark ? 'bg-purple-500/20 border-purple-500/50' : 'bg-purple-50 border-purple-100') : (isDark ? 'bg-white/5 border-transparent' : 'bg-white border-slate-200')}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold bg-gradient-to-br ${idx === 0 ? 'from-pink-500 to-purple-600' : 'from-cyan-400 to-blue-600'}`}>{ag.agent_name.charAt(0)}</div>
                  <div className="flex-1">
                    <div className="text-[11px] font-bold">{ag.agent_name} <span className="bg-purple-100 text-purple-700 px-1 rounded text-[8px]">{idx === 0 ? 'Orchestrator' : 'Router'}</span></div>
                    <div className="text-[9px] text-slate-500 capitalize">{ag.status} · Active</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* CENTER PANEL */}
        <section className={`flex-1 ${glassClass} rounded-3xl flex flex-col relative overflow-hidden backdrop-blur-[32px]`}>
          <div className="flex-1 overflow-y-auto p-8 space-y-8 hide-scrollbar">
            {chat.map((msg, idx) => (
              <div key={idx} className={`flex gap-4 max-w-2xl animate-fade-in ${msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg ${msg.role === 'user' ? 'border border-slate-300' : (isDark ? 'bg-cyan-900/50' : 'bg-blue-100')}`}>
                  {msg.role === 'user' ? (user?.avatar ? <img className="w-full h-full object-cover rounded-full" src={user.avatar} alt="u"/> : <div className="text-xs font-bold">G</div>) : <span className="material-symbols-outlined text-blue-600" style={{ fontVariationSettings: "'FILL' 1" }}>hub</span>}
                </div>
                <div className={`space-y-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`text-[10px] font-bold uppercase tracking-widest ${msg.role === 'user' ? 'text-slate-500' : 'text-blue-600'}`}>{msg.agentName || (msg.role === 'user' ? user?.display_name : 'System')}</div>
                  <div className={`${msg.role === 'user' ? (isDark ? 'bg-slate-800' : 'bg-white') : (isDark ? 'bg-cyan-900/40 text-slate-200' : 'bg-blue-50/80 text-slate-700')} border ${isDark ? 'border-white/10' : 'border-slate-200'} p-5 rounded-2xl text-sm font-medium shadow-sm leading-relaxed`}>
                     {msg.content}
                  </div>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          
          <div className="p-6">
            <div className={`${glassStrong} max-w-3xl mx-auto rounded-full p-2 flex items-center gap-4 transition-all focus-within:shadow-[0_0_20px_rgba(59,130,246,0.3)]`}>
              <button onClick={() => void send()} className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 shadow-[0_4px_15px_rgba(147,51,234,0.3)] hover:scale-105 transition-transform text-white">
                <span className="material-symbols-outlined font-bold text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>mic</span>
              </button>
              <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && void send()} className="flex-1 bg-transparent border-none text-slate-800 dark:text-white text-md font-bold focus:outline-none placeholder:text-slate-400" placeholder="Konuşarak veya yazarak komut ver..." />
              <button className={`px-5 py-2.5 rounded-full font-bold text-xs flex items-center gap-2 transition ${isDark ? 'bg-white/10 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                <span className="material-symbols-outlined text-sm">extension</span> Eklentiler
              </button>
            </div>
          </div>
        </section>

        {/* RIGHT PANEL */}
        <aside className={`w-[45%] ${glassClass} rounded-3xl overflow-hidden flex flex-col relative shadow-[0_0_30px_rgba(0,0,0,0.05)]`}>
          <div className={`p-4 border-b ${isDark ? 'border-white/10 bg-black/40' : 'border-slate-200 bg-white/50'} flex justify-between items-center z-10`}>
            <h2 className={`text-sm font-bold uppercase tracking-widest ${isDark ? 'text-cyan-400' : 'text-blue-700'}`}>Neural Graph Core</h2>
            <div className="flex gap-2">
              <button className={`${isDark ? 'bg-white/10 text-white' : 'bg-white text-slate-600'} hover:opacity-80 p-1.5 rounded-lg border border-slate-200 shadow-sm`}><span className="material-symbols-outlined text-sm">fullscreen</span></button>
            </div>
          </div>
          
          <div className={`flex-1 relative ${isDark ? 'bg-black/20' : 'bg-white/40'}`}>
            <ForceGraphViewer onNodeHover={(node) => setInspectNode(node)} />
            
            <div className={`absolute bottom-6 left-6 right-6 ${isDark ? 'bg-[#0f172a]/95 border-cyan-400/50' : 'bg-white/95 border-blue-200'} backdrop-blur-md border rounded-xl p-4 shadow-xl transition-all ${inspectNode ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`text-sm font-bold flex items-center gap-2 ${isDark ? 'text-cyan-400' : 'text-blue-600'}`}>
                  <span className={`w-2 h-2 rounded-full animate-pulse ${isDark ? 'bg-cyan-500' : 'bg-blue-500'}`}></span> Inspecting: {inspectNode?.name || 'Node'}.md
                </h4>
                <span className="text-[10px] font-mono text-slate-500">Source: Local AI</span>
              </div>
              <pre className={`text-[10px] font-mono p-3 rounded-lg border overflow-x-auto hide-scrollbar ${isDark ? 'bg-black/50 border-white/10 text-cyan-300' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
{JSON.stringify({
  "node": inspectNode?.name || "unknown",
  "val": inspectNode?.val?.toFixed(2) || "0.00",
  "integrity": 0.99,
  "last_synced": "2ms ago"
}, null, 2)}
              </pre>
            </div>
          </div>
        </aside>
      </main>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        * { font-family: 'Space Grotesk', sans-serif !important; }
        .material-symbols-outlined { font-family: 'Material Symbols Outlined' !important; }
      `}</style>
    </div>
  )
}
